/**
 * Shared error handling for `@sal/webrequest-web` calls.
 *
 * The web-request library does NOT throw on most non-2xx responses. Instead:
 * - 400 responses resolve with the parsed body, which may be an object (`{ message }` or ASP.NET
 *   `ProblemDetails`) or a bare JSON string (e.g. `BadRequest("At least one ID is required.")`).
 * - A 400 with an EMPTY body (e.g. `BadRequest()`) makes the library's `response.json()` throw a
 *   `SyntaxError`, so the promise rejects with an unhelpful parse message.
 * - Any other non-2xx (401, 403, 404, 500, ...) resolves with a synthetic envelope:
 *   `{ errors: [{ message: "Unexpected error. Please refresh page and try again." }] }`.
 *
 * Because most failures resolve rather than rejecting, callers must inspect the result and throw,
 * otherwise mutations silently appear to succeed. These helpers centralise that detection so every
 * hook handles 4xx/5xx errors the same way, regardless of the body shape the backend returned.
 */

export type ApiErrorResponse = {
	detail?: string;
	title?: string;
	message?: string;
	errors?: Array<{ message?: string }> | Record<string, string[]>;
};

const DEFAULT_FALLBACK = "Something went wrong. Please try again.";

function firstErrorMessage(errors: ApiErrorResponse["errors"]): string | undefined {
	if (Array.isArray(errors)) {
		return errors[0]?.message;
	}

	if (errors && typeof errors === "object") {
		const first = Object.values(errors)[0];
		if (Array.isArray(first)) {
			return first[0];
		}
	}

	return undefined;
}

/** True when a resolved web-request result is actually an error envelope rather than real data. */
export function isApiErrorResponse(response: unknown): response is ApiErrorResponse {
	// A 400 with a bare-string body resolves to a non-empty JSON string rather than an object.
	if (typeof response === "string") {
		return response.trim().length > 0;
	}

	if (!response || typeof response !== "object") {
		return false;
	}

	const candidateResponse = response as ApiErrorResponse;
	return (
		candidateResponse.errors != null ||
		typeof candidateResponse.detail === "string");
}

/** Extracts a human-readable message from a thrown error or an error-envelope response. */
export function getApiErrorMessage(response: unknown, fallback: string = DEFAULT_FALLBACK): string {
	// The library's `response.json()` throws a SyntaxError on an empty error body; its parse message
	// ("Unexpected end of JSON input") is not user-facing, so prefer the caller's fallback.
	if (response instanceof SyntaxError) {
		return fallback;
	}

	if (response instanceof Error) {
		return response.message.trim().length > 0 ? response.message : fallback;
	}

	// A 400 with a bare-string body resolves to the message string directly.
	if (typeof response === "string") {
		return response.trim().length > 0 ? response : fallback;
	}

	if (response && typeof response === "object") {
		const apiError = response as ApiErrorResponse;
		return (
			firstErrorMessage(apiError.errors) ??
			apiError.detail ??
			apiError.title ??
			apiError.message ??
			fallback
		);
	}

	return fallback;
}

/**
 * Throws when the resolved web-request result is an error envelope (a 4xx/5xx the library swallowed
 * instead of throwing). Returns the result narrowed to `T` otherwise.
 */
export function assertApiSuccess<T>(response: unknown, fallback: string = DEFAULT_FALLBACK): T {
	if (isApiErrorResponse(response)) {
		throw new Error(getApiErrorMessage(response, fallback));
	}

	return response as T;
}
